<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import useModal from '../../../../Composables/useModal.ts'
import { useUserLookup } from '../../../../Composables/User/useUserLookup'
import { isValidEmail } from '../../Utils/string'
import type { UserLookupFound } from '../../../../Types/user'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  entityId: number
}>()

const emit = defineEmits<{
  close: []
  found: [{ result: UserLookupFound; email: string }]
  notFound: [{ email: string }]
}>()

const { t } = useI18n()
const { modal, modalId, open, close } = useModal({ prefix: 'user-lookup-' })

const email = ref('')
const hasTriedSubmit = ref(false)

const { result, loading, error, lookup } = useUserLookup(() => props.entityId, email)

const inputId = `${modalId}-email`
const trimmedEmail = computed(() => email.value.trim())
const emailInvalid = computed(() => hasTriedSubmit.value && !isValidEmail(trimmedEmail.value))
const alreadyAttached = computed(() => !!result.value?.user && result.value.alreadyAttached === true)

async function search() {
  hasTriedSubmit.value = true
  if (!isValidEmail(trimmedEmail.value) || loading.value) {
    return
  }

  const res = await lookup()
  if (!res) {
    return
  }

  if (!res.user) {
    close()
    emit('notFound', { email: trimmedEmail.value })
    return
  }

  if (res.alreadyAttached) {
    return
  }

  close()
  emit('found', {
    result: { user: res.user, alreadyAttached: false },
    email: trimmedEmail.value,
  })
}

function cancel() {
  close()
  emit('close')
}

onMounted(() => {
  open()
})
</script>

<template>
  <ModalDialog
    :id="modalId"
    ref="modal"
    :title="t('userLookup.title')"
    class-body="px-3"
    e2e-modal="modal-user-lookup"
    @icon-close="emit('close')"
  >
    <template #body>
      <form novalidate @submit.prevent="search">
        <label :for="inputId">{{ t('userLookup.email.label') }}</label>
        <input
          :id="inputId"
          v-model="email"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': emailInvalid }"
          autocomplete="off"
          e2e-data="userLookupEmail"
        />
        <div v-if="emailInvalid" class="invalid-feedback">
          {{ t('userEntity.fields.user.email.invalid') }}
        </div>
      </form>
      <div v-if="alreadyAttached" class="alert alert-warning mt-3 mb-0">
        {{ t('userLookup.alreadyAttached') }}
      </div>
      <div v-if="error" class="alert alert-danger mt-3 mb-0">
        {{ t(error) }}
      </div>
    </template>
    <template #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" name="userLookupClose" @click="cancel" />
        <ButtonAll
          margin="no"
          type="action"
          label="userLookup.search"
          class="ml-2"
          name="userLookupSearch"
          :loading="loading"
          @click="search"
        />
      </div>
    </template>
  </ModalDialog>
</template>
